import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8083';

const BADGE_LABELS = {
  streamer: { text: 'STR', color: '#eb0400' },
  mod: { text: 'MOD', color: '#00c400' },
  sub: { text: 'SUB', color: '#D6E9AA' },
};

export default function ChatOverlay() {
  const { username } = useParams();
  const [searchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const bottomRef = useRef(null);

  // Customizable via URL params
  const fontSize = searchParams.get('fontSize') || '14';
  const maxMessages = parseInt(searchParams.get('max') || '25');
  const showBadges = searchParams.get('badges') !== 'false';
  const fadeOut = searchParams.get('fade') !== 'false';
  const fontFamily = searchParams.get('font') || "'Nunito', sans-serif";

  useEffect(() => {
    const socket = io(BASE, { transports: ['websocket', 'polling'] });

    socket.on('connect', () => {
      socket.emit('join', { channel: username, username: '' });
    });

    socket.on('message', (msg) => {
      const id = Date.now() + Math.random();
      setMessages(prev => [...prev.slice(-(maxMessages - 1)), { ...msg, _id: id, _ts: Date.now() }]);
    });

    return () => socket.disconnect();
  }, [username, maxMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fade out old messages
  useEffect(() => {
    if (!fadeOut) return;
    const interval = setInterval(() => {
      setMessages(prev => prev.filter(m => Date.now() - m._ts < 30000));
    }, 5000);
    return () => clearInterval(interval);
  }, [fadeOut]);

  return (
    <div style={{
      background: 'transparent',
      width: '100%',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      padding: '10px',
      overflow: 'hidden',
      fontFamily,
    }}>
      <style>{`
        body { background: transparent !important; margin: 0; overflow: hidden; }
        @keyframes chatSlideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .overlay-msg { animation: chatSlideIn 0.3s ease; }
        .overlay-msg.fading { opacity: 0.3; transition: opacity 1s; }
      `}</style>

      {messages.map((msg) => {
        const age = Date.now() - msg._ts;
        const fading = fadeOut && age > 25000;

        return (
          <div
            key={msg._id}
            className={`overlay-msg ${fading ? 'fading' : ''}`}
            style={{
              padding: '4px 8px',
              marginBottom: '4px',
              borderRadius: '4px',
              background: 'rgba(0,0,0,0.6)',
              fontSize: `${fontSize}px`,
              lineHeight: 1.4,
              wordBreak: 'break-word',
              maxWidth: '100%',
            }}
          >
            {msg.is_system ? (
              <span style={{ color: '#adadb8', fontStyle: 'italic' }}>{msg.message}</span>
            ) : (
              <>
                {/* Badges */}
                {showBadges && msg.badges?.length > 0 && msg.badges.map(b => {
                  const badge = BADGE_LABELS[b];
                  if (!badge) return null;
                  return (
                    <span key={b} style={{
                      display: 'inline-block',
                      fontSize: `${parseInt(fontSize) * 0.6}px`,
                      fontWeight: 800,
                      padding: '1px 4px',
                      borderRadius: '2px',
                      marginRight: '4px',
                      background: badge.color + '33',
                      color: badge.color,
                      verticalAlign: 'middle',
                    }}>{badge.text}</span>
                  );
                })}
                <span style={{
                  fontWeight: 700,
                  marginRight: '6px',
                  color: msg.badges?.includes('streamer') ? '#eb0400'
                    : msg.badges?.includes('mod') ? '#00c400'
                    : msg.verified ? '#D6E9AA' : '#ffb347',
                }}>
                  {msg.username}
                </span>
                <span style={{ color: '#efeff1' }}>
                  {msg.removed ? '<message removed>' : msg.message}
                </span>
              </>
            )}
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}
